/**
 * Mapping from technical object type names to user-friendly display names
 */

import type { IconName } from '@customerio/pluma-icons';
import { isEmptyString } from '@/utils/string';

// Account-level audit object types
export const accountObjectTypeMapping: Record<string, string> = {
  APICredentials: 'API Credentials',
  Account: 'Account',
  ExternalAPIKey: 'App API Key',
  IP: 'Dedicated IP Address',
  Role: 'Role',
  SCIMConfiguration: 'SCIM Configuration',
  SCIMToken: 'SCIM Token',
  SSOConfiguration: 'SSO Configuration',
  Session: 'Log in/out',
  User: 'Team Member',
  UserAccountPermissions: 'Team Member Account Permissions',
  WhitelistedIP: 'Whitelisted IP',
  Workspace: 'Workspace',
  WorkspacePermissions: 'Team Member Workspace Permissions',
};

// Category definitions for account-level audit object types
export const ACCOUNT_OBJECT_TYPE_CATEGORIES: Record<string, string[]> = {
  Permissions: ['User', 'UserAccountPermissions', 'WorkspacePermissions', 'Role'],
  Security: ['Session', 'SSOConfiguration', 'SCIMConfiguration', 'SCIMToken', 'WhitelistedIP'],
  'API & Integrations': ['APICredentials', 'ExternalAPIKey', 'IP'],
  Administration: ['Account', 'Workspace'],
};

// Category definitions for workspace-level audit object types
export const WORKSPACE_OBJECT_TYPE_CATEGORIES: Record<string, string[]> = {
  Messaging: [
    'Campaign',
    'APITriggeredBroadcast',
    'CampaignAction',
    'Newsletter',
    'TransactionalMessage',
    'InAppBroadcast',
    'JourneyExit',
  ],
  'Segments & Audiences': ['Segment', 'Audience', 'AudienceCredentials'],
  Content: ['Template', 'Layout', 'Snippet', 'SavedRow', 'Asset'],
  'Design Studio': ['DesignStudioGlobalStyles', 'DesignStudioNode', 'DesignStudioVersion'],
  'Data & Imports': ['Import', 'Export', 'SQLImport', 'SQLImportDatabaseDefinition', 'Collection'],
  'People & Objects': ['ProfileDeletion', 'ProfileMerge', 'ProfileShortcut', 'ObjectDeletion', 'ObjectType'],
  Integrations: ['Integration', 'WebhookConfiguration', 'CustomSMTP', 'CustomForm'],
  Settings: ['Domain', 'SenderIdentity', 'SubscriptionCenterSettings', 'SubscriptionTopic', 'Tag'],
};

// Object types that should not display object IDs in activity rows or filters
export const NO_OBJECT_ID_TYPES = new Set(['DesignStudioGlobalStyles', 'Account']);

// Workspace-level audit object types
export const workspaceObjectTypeMapping: Record<string, string> = {
  Asset: 'Asset',
  Audience: 'Ad Audience',
  AudienceCredentials: 'Ad Audience Credentials',
  APITriggeredBroadcast: 'API-Triggered Broadcast',
  Campaign: 'Campaign',
  CampaignAction: 'Campaign Action',
  Collection: 'Collection',
  Credentials: 'Credentials',
  CustomForm: 'Custom Form',
  CustomSMTP: 'Custom SMTP',
  Condition: 'Segment Condition',
  DesignStudioGlobalStyles: 'Design Studio Global Styles',
  DesignStudioNode: 'Design Studio Element',
  DesignStudioVersion: 'Design Studio Version',
  Domain: 'Domain',
  Export: 'Export',
  Import: 'Import',
  InAppBroadcast: 'Anonymous Message',
  Integration: 'Integration',
  JourneyExit: 'End Journey',
  Layout: 'Layout',
  Newsletter: 'Newsletter',
  ObjectDeletion: 'Object Deletion',
  ObjectType: 'Object Type',
  ProfileDeletion: 'Profile Deletion',
  ProfileMerge: 'Profile Merge',
  ProfileShortcut: 'Profile Shortcut',
  SQLImport: 'SQL Import',
  SQLImportDatabaseDefinition: 'SQL Import Database Definition',
  SavedRow: 'Saved Row',
  Segment: 'Segment',
  SenderIdentity: 'Sender Identity',
  Snippet: 'Snippet',
  SubscriptionCenterSettings: 'Subscription Center Settings',
  SubscriptionTopic: 'Subscription Topic',
  Tag: 'Tag',
  Template: 'Template',
  TransactionalMessage: 'Transactional Message',
  WebhookConfiguration: 'Webhook Configuration',
};

/**
 * Get user-friendly name for an object type based on context
 */
export function getObjectTypeDisplayName(objectType: string, context: 'account' | 'workspace'): string {
  const mapping = context === 'account' ? accountObjectTypeMapping : workspaceObjectTypeMapping;
  return !isEmptyString(mapping[objectType]) ? mapping[objectType] : objectType;
}

/**
 * Get all available object types for a context as dropdown options
 */
export function getObjectTypeOptions(context: 'account' | 'workspace'): Array<{ value: string; label: string }> {
  const mapping = context === 'account' ? accountObjectTypeMapping : workspaceObjectTypeMapping;

  return [
    { value: '', label: 'All Object Types' },
    ...Object.entries(mapping)
      .sort(([, a], [, b]) => a.localeCompare(b))
      .map(([key, value]) => ({ value: key, label: value })),
  ];
}

export interface SelectOptionItem {
  value: string;
  label: string;
  icon?: IconName;
  description?: string;
}

export interface SelectOptionGroup {
  label: string;
  options: SelectOptionItem[];
  icon?: IconName;
  isCollapsible?: boolean;
  defaultIsCollapsed?: boolean;
}

/**
 * Descriptions for specific object types shown as subtitle in dropdown.
 */
const OBJECT_TYPE_DESCRIPTIONS: Record<string, string> = {
  DesignStudioNode: 'Folder, Component, Template or Email',
};

/**
 * Icon mappings for category groups in the Event Type dropdown.
 */
const CATEGORY_ICONS: Record<string, IconName> = {
  // Workspace categories
  Content: 'layout',
  'Data & Imports': 'database',
  'Design Studio': 'design-studio',
  Integrations: 'connections',
  Messaging: 'chat',
  'People & Objects': 'person',
  'Segments & Audiences': 'segment',
  Settings: 'manage',
  // Account categories
  Administration: 'company',
  'API & Integrations': 'api-credentials',
  Permissions: 'user-id',
  Security: 'lock-secure',
};

/**
 * Get grouped object type options for the given context.
 * Returns options grouped by category with collapsible sections.
 */
export function getGroupedObjectTypeOptions(context: 'account' | 'workspace'): SelectOptionGroup[] {
  const categories = context === 'account' ? ACCOUNT_OBJECT_TYPE_CATEGORIES : WORKSPACE_OBJECT_TYPE_CATEGORIES;
  const mapping = context === 'account' ? accountObjectTypeMapping : workspaceObjectTypeMapping;

  const groups: SelectOptionGroup[] = [];

  // Sort categories alphabetically
  const sortedCategories = Object.entries(categories).sort(([a], [b]) => a.localeCompare(b));

  for (const [categoryName, objectTypes] of sortedCategories) {
    const options: SelectOptionItem[] = [];

    // Sort object types within each category by display name
    const sortedObjectTypes = objectTypes
      .map((objectType) => ({
        objectType,
        displayName: mapping[objectType] ?? objectType,
      }))
      .sort((a, b) => a.displayName.localeCompare(b.displayName));

    for (const { objectType, displayName } of sortedObjectTypes) {
      const option: SelectOptionItem = {
        value: objectType,
        label: displayName,
      };
      // Add description if available
      if (OBJECT_TYPE_DESCRIPTIONS[objectType] != null) {
        option.description = OBJECT_TYPE_DESCRIPTIONS[objectType];
      }
      options.push(option);
    }

    groups.push({
      label: categoryName,
      options,
      icon: CATEGORY_ICONS[categoryName],
      isCollapsible: true,
      defaultIsCollapsed: false,
    });
  }

  return groups;
}

const ACTIONS = ['created', 'updated', 'deleted'] as const;
const ACTION_LABELS: Record<string, string> = {
  created: 'Created',
  updated: 'Updated',
  deleted: 'Deleted',
};

/**
 * Get action options for the action filter dropdown
 * Returns options with special labels for Session and User events
 */
export function getActionOptions(objectType?: string): Array<{ value: string; label: string }> {
  // Determine which labels to use based on object type
  let actionLabels = ACTION_LABELS;
  if (objectType === 'Session') {
    actionLabels = SESSION_ACTION_LABELS;
  } else if (objectType === 'User') {
    actionLabels = USER_ACTION_LABELS;
  }

  return ACTIONS.map((action) => ({
    value: action,
    label: actionLabels[action],
  }));
}

// Special labels for Session events (login/logout)
const SESSION_ACTION_LABELS: Record<string, string> = {
  created: 'Login',
  updated: 'Updated',
  deleted: 'Logout',
};

// Special labels for User events (team member added/removed)
const USER_ACTION_LABELS: Record<string, string> = {
  created: 'Team Member Added',
  updated: 'Updated',
  deleted: 'Team Member Removed',
};

// Special labels for JourneyExit events
const JOURNEY_EXIT_ACTION_LABELS: Record<string, string> = {
  created: 'Ended Journey',
  updated: 'Updated',
  deleted: 'Deleted',
};

/**
 * Get human-friendly activity text for an audit log event
 * @param objectType - The object type (e.g., "Session", "User")
 * @param action - The action (e.g., "created", "updated", "deleted")
 * @param context - The context ('account' or 'workspace')
 * @returns Object with verb and objectName for constructing activity text
 */
export function getActivityText(
  objectType: string,
  action: string,
  context: 'account' | 'workspace',
): { verb: string; objectName: string } {
  const actionLower = action.toLowerCase();

  // Special handling for Session events
  if (objectType === 'Session') {
    if (actionLower === 'created') {
      return { verb: 'logged in', objectName: '' };
    }
    if (actionLower === 'deleted') {
      return { verb: 'logged out', objectName: '' };
    }
    // For updated, show normal format
    return { verb: 'updated', objectName: 'Session' };
  }

  // Special handling for User events - map to "team member" terminology
  if (objectType === 'User') {
    if (actionLower === 'created') {
      return { verb: 'added', objectName: 'a new team member' };
    }
    if (actionLower === 'deleted') {
      return { verb: 'removed', objectName: 'a team member' };
    }
    // For updated, show "team member" instead of "User"
    return { verb: 'updated', objectName: 'team member' };
  }

  // Special handling for UserAccountPermissions
  if (objectType === 'UserAccountPermissions') {
    let verb = actionLower;
    if (actionLower === 'created') verb = 'set';
    else if (actionLower === 'updated') verb = 'updated';
    else if (actionLower === 'deleted') verb = 'deleted';
    return { verb, objectName: 'account permissions' };
  }

  // Special handling for WorkspacePermissions
  if (objectType === 'WorkspacePermissions') {
    let verb = actionLower;
    if (actionLower === 'created') verb = 'set';
    else if (actionLower === 'updated') verb = 'updated';
    else if (actionLower === 'deleted') verb = 'deleted';
    return { verb, objectName: 'workspace permissions' };
  }

  // Special handling for JourneyExit - show "ended journey" instead of "added end journey"
  if (objectType === 'JourneyExit') {
    if (actionLower === 'created') {
      return { verb: 'ended', objectName: 'journey' };
    }
    // For other actions, use standard format with "End Journey" display name
    return { verb: actionLower, objectName: 'End Journey' };
  }

  // Normal handling for other object types
  const objectName = getObjectTypeDisplayName(objectType, context);
  let verb = actionLower;
  if (actionLower === 'created') verb = 'added';
  else if (actionLower === 'updated') verb = 'updated';
  else if (actionLower === 'deleted') verb = 'deleted';

  return { verb, objectName };
}

/**
 * Get all available object types with action combinations for a context
 * Each object type gets 3 options: Created, Updated, Deleted
 * Value format: "ObjectType:action" (e.g., "User:created")
 */
export function getObjectTypeWithActionOptions(
  context: 'account' | 'workspace',
): Array<{ value: string; label: string }> {
  const mapping = context === 'account' ? accountObjectTypeMapping : workspaceObjectTypeMapping;

  const options: Array<{ value: string; label: string }> = [];

  // Sort object types alphabetically by display name
  const sortedEntries = Object.entries(mapping).sort(([, a], [, b]) => a.localeCompare(b));

  for (const [objectType, displayName] of sortedEntries) {
    // Special handling for Session, User, and JourneyExit - use custom labels
    const actionLabels =
      objectType === 'Session'
        ? SESSION_ACTION_LABELS
        : objectType === 'User'
        ? USER_ACTION_LABELS
        : objectType === 'JourneyExit'
        ? JOURNEY_EXIT_ACTION_LABELS
        : ACTION_LABELS;

    for (const action of ACTIONS) {
      // For Session login/logout, don't prefix with "Session"
      // For User added/removed, the label already includes "Team Member"
      // For JourneyExit created, use "Ended Journey" label directly
      let label: string;
      if (objectType === 'Session' && action !== 'updated') {
        label = actionLabels[action];
      } else if (objectType === 'User' && action !== 'updated') {
        label = actionLabels[action];
      } else if (objectType === 'JourneyExit' && action === 'created') {
        label = actionLabels[action];
      } else {
        label = `${displayName} ${actionLabels[action]}`;
      }

      options.push({
        value: `${objectType}:${action}`,
        label,
      });
    }
  }

  return options;
}

/**
 * Parse a combined object type + action value
 * @param combinedValue - Value in format "ObjectType:action" (e.g., "User:created")
 * @returns Object with objectType and action, or null if invalid
 */
export function parseObjectTypeActionValue(combinedValue: string): { objectType: string; action: string } | null {
  if (isEmptyString(combinedValue) || !combinedValue.includes(':')) {
    return null;
  }
  // Split at first colon only (handles edge case of value containing colons)
  const colonIndex = combinedValue.indexOf(':');
  const objectType = combinedValue.substring(0, colonIndex);
  const action = combinedValue.substring(colonIndex + 1);

  // Validate both parts are non-empty
  if (isEmptyString(objectType) || isEmptyString(action)) {
    return null;
  }
  return { objectType, action };
}
